<script>
import moment from "moment";
import axios from "@/utils/axios";
import "daterangepicker/daterangepicker.js";
import "daterangepicker/daterangepicker.css";
import DateRangePicker from "daterangepicker";
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";
import { clientAdmin } from "@/utils/auth";

export default {
  inject: ["setLoading"],
  components: { Vue3ChartJs },
  data() {
    return {
      errors: {},
      receiptsList: [],
      selectedPO: null,
      title: "Reports",
      text: "Inventory",
      text1: "Purchase Order Report",
      app_currency: "",
      previewFiles: [],
      filter: {
        from_date: "",
        to_date: "",
        warehouse_id: "",
        vendor_id: "",
        status: "",
      },
      summary: {},
      data: [],
      warehouses: [],
      vendors: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: [],
      },
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    statusBarChart() {
      if (!this.summary || !this.summary.status_counts) return null;
      const sc = this.summary.status_counts;
      const labels = [];
      const values = [];
      const statusMap = {
        draft: "Draft",
        approved: "Approved",
        received: "Received",
        partial: "Partial",
        partial_pending: "Partial Pending",
        completed: "Completed",
      };
      for (const key in sc) {
        labels.push(statusMap[key] || key);
        values.push(sc[key]);
      }
      if (!labels.length) return null;
      const colors = [
        "#4f46e5",
        "#06b6d4",
        "#f59e0b",
        "#10b981",
        "#ef4444",
        "#8b5cf6",
      ];
      return {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Orders",
              data: values,
              backgroundColor: labels.map((_, i) => colors[i % colors.length]),
              borderRadius: 6,
              barThickness: 40,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: { stepSize: 1 },
            },
            x: { grid: { display: false } },
          },
        },
      };
    },

    warehouseDoughnutChart() {
      const map = {};
      (this.data || []).forEach((row) => {
        const name = row.warehouse?.name || "Unknown";
        const amt = parseFloat(row.total_amount || 0);
        map[name] = (map[name] || 0) + amt;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      if (!labels.length) return null;
      const colors = [
        "#4f46e5",
        "#06b6d4",
        "#f59e0b",
        "#10b981",
        "#ef4444",
        "#8b5cf6",
      ];
      return {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: labels.map((_, i) => colors[i % colors.length]),
              borderWidth: 2,
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                padding: 15,
                usePointStyle: true,
                pointStyle: "circle",
              },
            },
          },
          cutout: "65%",
        },
      };
    },

    vendorDoughnutChart() {
      const map = {};
      (this.data || []).forEach((row) => {
        const name = row.vendor?.name || "Unknown";
        const amt = parseFloat(row.total_amount || 0);
        map[name] = (map[name] || 0) + amt;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      if (!labels.length) return null;
      const colors = [
        "#10b981",
        "#f59e0b",
        "#4f46e5",
        "#ef4444",
        "#06b6d4",
        "#8b5cf6",
      ];
      return {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: labels.map((_, i) => colors[i % colors.length]),
              borderWidth: 2,
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                padding: 15,
                usePointStyle: true,
                pointStyle: "circle",
              },
            },
          },
          cutout: "65%",
        },
      };
    },
  },

  async mounted() {
    this.app_currency = await this.$appCurrency();
    this.getDropdowns();

    const start = moment().subtract(29, "days");
    const end = moment();

    this.filter.from_date = start.format("YYYY-MM-DD");
    this.filter.to_date = end.format("YYYY-MM-DD");

    new DateRangePicker(
      this.$refs.dateRangeInput,
      {
        startDate: start,
        endDate: end,
        autoUpdateInput: false,
        locale: {
          format: "DD/MM/YYYY",
          separator: " - ",
          cancelLabel: "Clear",
        },
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [
            moment().subtract(1, "days"),
            moment().subtract(1, "days"),
          ],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      (start, end) => {
        this.filter.from_date = start.format("YYYY-MM-DD");
        this.filter.to_date = end.format("YYYY-MM-DD");
        this.$refs.dateRangeInput.value =
          start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        this.filterReport();
      },
    );

    this.filterReport();
  },

  methods: {

    getReceiptInvoice(receiptId) {
  if (!this.selectedPO || !this.selectedPO.receipts) return null;
  return this.selectedPO.receipts.find(r => r.id === receiptId) || null;
},


    varianceSummary(row) {
      let approved = 0, rejected = 0, pending = 0;
      (row.purchase_order_items || []).forEach(item => {
        (item.purchase_receipt_items || []).forEach(pri => {
          if (pri.stock_approval) {
            if (pri.stock_approval.status === 'approved') approved++;
            else if (pri.stock_approval.status === 'rejected') rejected++;
            else pending++;
          }
        });
      });
      return { approved, rejected, pending, total: approved + rejected + pending };
    },


viewReceipts(row) {
  this.selectedPO = row;
  this.$openModal("po_detail_modal");
},

  viewInvoiceImage(receipt) {
    this.previewFiles = receipt.files || [];
    this.$closeModal("po_detail_modal");
    setTimeout(() => {
      this.$openModal("invoice_image_modal");
    }, 300);
  },

    viewPurchaseOrder(public_token) {
      const route = this.$router.resolve({
        name: "view-purchase-order",
        params: { public_token },
      });
      window.open(route.href, "_blank");
    },

    async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv' };
        if (this.filter.from_date) payload.date_from = this.filter.from_date;
        if (this.filter.to_date) payload.date_to = this.filter.to_date;
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.status) payload.status = this.filter.status;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/purchase-orders?${query_string}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `purchase-order-report-${this.filter.from_date}-to-${this.filter.to_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async filterReport(page = 1) {
      this.errors = {};
      this.setLoading(true);
      try {
        const payload = {
          page,
          date_from: this.filter.from_date,
          date_to: this.filter.to_date,
        };
        if (this.filter.warehouse_id)
          payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.status) payload.status = this.filter.status;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(
          `/reports/purchase-orders?${query_string}`,
        );
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links,
        };
      } catch (error) {
        if (error.type == "validation") {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, "error");
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      try {
        const [warehouseRes, vendorRes] = await Promise.all([
          axios.get("/inventory/warehouses/dropdown"),
          axios.get("/inventory/vendors/dropdown"),
        ]);
        this.warehouses = warehouseRes.data.data;
        this.vendors = vendorRes.data.data;
      } catch (error) {
        console.log("Dropdown load error:", error.message);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return (
        this.filter.warehouse_id ||
        this.filter.vendor_id ||
        this.filter.status ||
        this.filter.from_date ||
        this.filter.to_date
      );
    },

    clearFilter() {
      this.filter.warehouse_id = "";
      this.filter.vendor_id = "";
      this.filter.status = "";
      this.filter.from_date = "";
      this.filter.to_date = "";
      this.$refs.dateRangeInput.value = "";
      this.filterReport();
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
      });
    },

    formatQty(val) {
      const num = parseFloat(val || 0);
      return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    },

    formatStatus(status) {
      if (!status) return "-";
      const map = {
        draft: "Draft",
        approved: "Approved",
        received: "Received",
        partial: "Partial",
        partial_pending: "Partial Pending",
        completed: "Completed",
      };
      return map[status] || status;
    },

    statusBadgeClass(status) {
      const map = {
        draft: "bg-light text-dark",
        approved: "bg-info text-white",
        received: "bg-primary text-white",
        partial: "bg-warning text-dark",
        partial_pending: "bg-danger text-white",
        completed: "bg-success text-white",
      };
      return map[status] || "bg-secondary text-white";
    },

    changePctClass(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return "text-success";
      if (num < 0) return "text-danger";
      return "text-muted";
    },

    changePctIcon(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return "ti ti-trending-up";
      if (num < 0) return "ti ti-trending-down";
      return "ti ti-minus";
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV"
            :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_orders">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Orders</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{
                summary.total_orders
              }}</span>
              <span v-if="summary.order_change_pct != null" :class="changePctClass(summary.order_change_pct)"
                style="font-size: 11px">
                <i :class="changePctIcon(summary.order_change_pct)" style="font-size: 12px"></i>
                {{ Math.abs(summary.order_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Amount</span>
              <span class="fw-bold text-success" style="font-size: 20px">{{
                formatCurrency(summary.total_amount)
              }}</span>
              <span v-if="summary.value_change_pct != null" :class="changePctClass(summary.value_change_pct)"
                style="font-size: 11px">
                <i :class="changePctIcon(summary.value_change_pct)" style="font-size: 12px"></i>
                {{ Math.abs(summary.value_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Total Tax</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{
                formatCurrency(summary.total_tax)
              }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Fulfilment Rate</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ summary.fulfilment_rate }}%</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Qty Ordered</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ formatQty(summary.total_qty_ordered) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Qty Received</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{ formatQty(summary.total_qty_received) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Unique Vendors</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{
                summary.unique_vendors
              }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Avg Order Value</span>
              <span class="fw-bold text-dark" style="font-size: 20px">{{
                formatCurrency(summary.avg_order_value)
              }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6" v-if="summary.variance_summary">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Variance OK</span>
              <span class="fw-bold text-success" style="font-size: 20px">{{
                summary.variance_summary.total_ok
              }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6" v-if="summary.variance_summary">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Variance Approved</span>
              <span class="fw-bold text-info" style="font-size: 20px">{{
                summary.variance_summary.total_approved
              }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6" v-if="summary.variance_summary">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">Variance Rejected</span>
              <span class="fw-bold text-danger" style="font-size: 20px">{{
                summary.variance_summary.total_rejected
              }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6" v-if="summary.variance_summary">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size: 11px">POs with Variance</span>
              <span class="fw-bold text-warning" style="font-size: 20px">{{
                summary.variance_summary.pos_with_variance
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row g-3 mb-3" v-if="data && data.length">
        <div class="col-md-6">
          <div class="card mb-0 border-0 shadow-sm" v-if="statusBarChart">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0">
                <i class="ti ti-chart-bar me-1 text-primary"></i>Orders by
                Status
              </h6>
            </div>
            <div class="card-body" style="height: 280px">
              <vue3-chart-js v-bind="statusBarChart" :key="'bar-' + data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm" v-if="warehouseDoughnutChart">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0">
                <i class="ti ti-building-warehouse me-1 text-primary"></i>By
                Warehouse
              </h6>
            </div>
            <div class="card-body" style="height: 280px">
              <vue3-chart-js v-bind="warehouseDoughnutChart" :key="'wh-' + data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm" v-if="vendorDoughnutChart">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0">
                <i class="ti ti-building-store me-1 text-primary"></i>By Vendor
              </h6>
            </div>
            <div class="card-body" style="height: 280px">
              <vue3-chart-js v-bind="vendorDoughnutChart" :key="'vn-' + data.length" />
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i
                  class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input type="text" class="form-control date-range bookingrange" ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy" />
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.warehouse_id"
                :options="warehouses"
                label="name"
                :reduce="w => w.id"
                placeholder="Warehouse"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.vendor_id"
                :options="vendors"
                label="name"
                :reduce="v => v.id"
                placeholder="Vendor"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.status"
                :options="[
                  { label: 'Draft', value: 'draft' },
                  { label: 'Approved', value: 'approved' },
                  { label: 'Received', value: 'received' },
                  { label: 'Partial', value: 'partial' },
                  { label: 'Partial Pending', value: 'partial_pending' },
                  { label: 'Completed', value: 'completed' },
                ]"
                label="label"
                :reduce="s => s.value"
                placeholder="Status"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>PO Number</th>
                    <th>Vendor</th>
                    <th>Warehouse</th>
                    <th>Status</th>
                    <th>Items</th>
                    <th>Invoice No</th>
                    <th>Qty Ord / Recv</th>
                    <th>Subtotal</th>
                    <th>Tax</th>
                    <th>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td>{{ formatDate(row.created_at) }}</td>
                    <td>
                      <a href="javascript:void(0);" class="text-primary fw-bold"
                        @click="viewPurchaseOrder(row.public_token)">{{ row.po_number }}</a>
                    </td>
                    <td>{{ row.vendor?.name || "-" }}</td>
                    <td>
                      <span class="badge bg-light text-primary border rounded-pill">{{ row.warehouse?.name || "-"
                        }}</span>
                    </td>
                    <td>
                      <span class="badge rounded-pill" :class="statusBadgeClass(row.status)">
                        {{ formatStatus(row.status) }}
                      </span>
                      <!-- Variance status check -->
                      <template v-if="varianceSummary(row).total > 0">
                        <span v-if="varianceSummary(row).approved" class="badge rounded-pill bg-success ms-1"
                          style="font-size:10px;">
                          {{ varianceSummary(row).approved }} Approved
                        </span>
                        <span v-if="varianceSummary(row).rejected" class="badge rounded-pill bg-danger ms-1"
                          style="font-size:10px;">
                          {{ varianceSummary(row).rejected }} Rejected
                        </span>
                        <span v-if="varianceSummary(row).pending" class="badge rounded-pill bg-warning text-dark ms-1"
                          style="font-size:10px;">
                          {{ varianceSummary(row).pending }} Pending
                        </span>
                      </template>
                    </td>
                    <td>{{ (row.purchase_order_items || []).length }}</td>
                    <td>
  <div v-if="row.receipts && row.receipts.length">
    <a href="javascript:void(0);" class="badge bg-light text-dark"
      style="font-size: 11px; cursor: pointer" @click="viewReceipts(row)">
      {{ row.receipts.length }} {{ row.receipts.length === 1 ? 'Receipt' : 'Receipts' }}
      <i class="ti ti-eye ms-1"></i>
    </a>
  </div>
  <span v-else class="text-muted">-</span>
</td>
                    <td>
  <span>{{ formatQty(row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.quantity_ordered || 0), 0)) }}</span>
  /
  <span :class="row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.received_quantity || 0), 0) < row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.quantity_ordered || 0), 0) ? 'text-danger fw-bold' : 'text-success fw-bold'">
    {{ formatQty(row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.received_quantity || 0), 0)) }}
  </span>
  <div class="progress mt-1" style="height:3px;">
    <div class="progress-bar" role="progressbar"
      :style="{ width: Math.min(100, Math.round((row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.received_quantity || 0), 0) / Math.max(1, row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.quantity_ordered || 0), 0))) * 100)) + '%' }"
      :class="row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.received_quantity || 0), 0) >= row.purchase_order_items?.reduce((sum, i) => sum + parseFloat(i.quantity_ordered || 0), 0) ? 'bg-success' : 'bg-warning'">
    </div>
  </div>
</td>
                    <td>{{ formatCurrency(row.subtotal) }}</td>
                    <td>{{ formatCurrency(row.tax_amount) }}</td>
                    <td>
                      <strong>{{ formatCurrency(row.total_amount) }}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">
                No records found.
              </div>

              <!-- Pagination -->
              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index" :class="[
                    'page-item',
                    { active: link.active, disabled: !link.url },
                  ]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label"
                      @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
    <app-footer></app-footer>
  </div>

  <!-- Image Preview Modal -->
<!-- Invoice Files Preview Modal -->
<div class="modal fade" id="invoice_image_modal">
  <div class="modal-dialog modal-dialog-centered modal-lg">
    <div class="modal-content" style="border: none; border-radius: 12px; overflow: hidden">
      <div class="modal-header border-0 pb-0">
        <h5 class="modal-title fw-bold">
          Invoice Files
          <span v-if="previewFiles.length" style="font-size:12px; color:#94a3b8; font-weight:500;">
            ({{ previewFiles.length }})
          </span>
        </h5>
        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
      </div>
      <div class="modal-body p-3" style="max-height:75vh; overflow-y:auto;">

        <div v-if="previewFiles.length" style="display:flex; flex-direction:column; gap:16px;">
          <div v-for="(file, i) in previewFiles" :key="i">

            <!-- File label -->
            <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:6px;">
              <span style="font-size:12px; font-weight:600; color:#64748b;">
                File {{ i + 1 }}
              </span>
              <a :href="file.document_url" target="_blank"
                style="font-size:11px; color:#2563eb; text-decoration:none; display:inline-flex; align-items:center; gap:3px;">
                <i class="ti ti-external-link" style="font-size:12px;"></i> Open
              </a>
            </div>

            <!-- PDF -->
            <div v-if="file.type === 'application/pdf'" style="text-align:center;">
              <a :href="file.document_url" target="_blank"
                style="display:inline-flex; flex-direction:column; align-items:center; gap:8px; padding:24px; background:#fef2f2; border:1px solid #fecaca; border-radius:10px; text-decoration:none; color:#ef4444; width:100%;">
                <i class="ti ti-file-type-pdf" style="font-size:40px;"></i>
                <span style="font-size:12px; font-weight:600;">Open PDF</span>
              </a>
            </div>

            <!-- Image -->
            <img v-else :src="file.document_url" alt="Invoice"
              style="max-width:100%; max-height:70vh; border-radius:8px; border:1px solid #e2e8f0;" />
          </div>
        </div>

        <div v-else style="text-align:center; padding:30px; color:#94a3b8;">
          No files available.
        </div>

      </div>
    </div>
  </div>
</div>

 <!-- ═══════════════════════════════════════════════
     PO DETAIL MODAL — Replaces old receipts_list_modal
═══════════════════════════════════════════════ -->
<div class="modal fade" id="po_detail_modal">
  <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
    <div class="modal-content" style="border: none; border-radius: 14px; overflow: hidden;" v-if="selectedPO">

      <!-- Header -->
      <div class="modal-header" style="background: linear-gradient(135deg, #0f172a, #1e3a5f); border: none; padding: 16px 22px;">
        <div>
          <h6 class="modal-title" style="color: #fff; font-weight: 700; font-size: 15px; margin: 0;">
            {{ selectedPO.po_number }}
          </h6>
          <div style="font-size: 11px; color: #94a3b8; margin-top: 3px;">
            {{ selectedPO.vendor?.name }} &bull; {{ selectedPO.warehouse?.name }} &bull; {{ formatDate(selectedPO.created_at) }}
          </div>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span class="badge rounded-pill" :class="statusBadgeClass(selectedPO.status)" style="font-size: 11px;">
            {{ formatStatus(selectedPO.status) }}
          </span>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" style="opacity: .7;"></button>
        </div>
      </div>

      <div class="modal-body p-0" style="background: #f8fafc;">

        <!-- PO Summary Cards -->
        <div style="padding: 16px 22px 0;">
          <div class="row g-2 mb-3">
            <div class="col-md-3 col-6">
              <div style="background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; text-align: center;">
                <div style="font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; font-weight: 600;">Subtotal</div>
                <div style="font-size: 16px; font-weight: 700; color: #1e293b; margin-top: 2px;">{{ formatCurrency(selectedPO.subtotal) }}</div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div style="background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; text-align: center;">
                <div style="font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; font-weight: 600;">Tax</div>
                <div style="font-size: 16px; font-weight: 700; color: #2563eb; margin-top: 2px;">{{ formatCurrency(selectedPO.tax_amount) }}</div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div style="background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; text-align: center;">
                <div style="font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; font-weight: 600;">Total Amount</div>
                <div style="font-size: 16px; font-weight: 700; color: #16a34a; margin-top: 2px;">{{ formatCurrency(selectedPO.total_amount) }}</div>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div style="background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; text-align: center;">
                <div style="font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; font-weight: 600;">Receipts</div>
                <div style="font-size: 16px; font-weight: 700; color: #1e293b; margin-top: 2px;">{{ (selectedPO.receipts || []).length }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Item-wise Breakdown -->
        <div style="padding: 0 22px 16px;">
          <h6 style="font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 10px; display: flex; align-items: center; gap: 6px;">
            <i class="ti ti-list-details" style="color: #f59e0b;"></i> Item-wise Breakdown
          </h6>

          <div v-for="(poItem, itemIdx) in selectedPO.purchase_order_items" :key="poItem.id"
            style="background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; margin-bottom: 12px; overflow: hidden;">

            <!-- Item Header -->
            <div style="padding: 12px 16px; border-bottom: 1px solid #f1f5f9; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 10px;">
                <span style="width: 24px; height: 24px; background: #eff6ff; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: #2563eb;">
                  {{ itemIdx + 1 }}
                </span>
                <div>
                  <span style="font-size: 13px; font-weight: 700; color: #1e293b;">{{ poItem.item?.name || '-' }}</span>
                  <span style="font-size: 10px; color: #94a3b8; margin-left: 6px;">{{ poItem.item?.code }}</span>
                </div>
              </div>
              <div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">
                <div style="text-align: center;">
                  <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">Ordered</div>
                  <span style="font-size: 13px; font-weight: 700; color: #1e293b;">{{ formatQty(poItem.quantity_ordered) }}</span>
                </div>
                <div style="text-align: center;">
                  <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">Received</div>
                  <span style="font-size: 13px; font-weight: 700;" :style="parseFloat(poItem.received_quantity) >= parseFloat(poItem.quantity_ordered) ? 'color:#16a34a;' : 'color:#ef4444;'">
                    {{ formatQty(poItem.received_quantity) }}
                  </span>
                </div>
                <div style="text-align: center;">
                  <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">Unit Price</div>
                  <span style="font-size: 13px; font-weight: 700; color: #2563eb;">{{ formatCurrency(poItem.unit_price) }}</span>
                </div>
                <div style="text-align: center;">
                  <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">Tax</div>
                  <span style="font-size: 11px; font-weight: 600; color: #64748b;">{{ poItem.tax?.name || 'None' }}</span>
                </div>
                <span class="badge rounded-pill"
                  :class="{
                    'bg-success text-white': poItem.receive_status === 'completed',
                    'bg-warning text-dark': poItem.receive_status === 'partially_received',
                    'bg-secondary text-white': poItem.receive_status === 'pending'
                  }" style="font-size: 10px;">
                  {{ poItem.receive_status === 'completed' ? 'Completed' : poItem.receive_status === 'partially_received' ? 'Partial' : 'Pending' }}
                </span>
              </div>
            </div>

            <!-- Progress Bar -->
            <div style="padding: 0 16px;">
              <div class="progress" style="height: 4px; margin: 8px 0;">
                <div class="progress-bar" role="progressbar"
                  :style="{ width: Math.min(100, Math.round((parseFloat(poItem.received_quantity) / Math.max(1, parseFloat(poItem.quantity_ordered))) * 100)) + '%' }"
                  :class="parseFloat(poItem.received_quantity) >= parseFloat(poItem.quantity_ordered) ? 'bg-success' : 'bg-warning'">
                </div>
              </div>
            </div>

            <!-- Receipt Items Table -->
            <div style="padding: 8px 16px 12px;" v-if="poItem.purchase_receipt_items && poItem.purchase_receipt_items.length">
              <div style="font-size: 10px; font-weight: 600; color: #64748b; margin-bottom: 6px; text-transform: uppercase; letter-spacing: .3px;">
                Receipt History
              </div>
              <table class="table table-sm mb-0" style="font-size: 11.5px;">
                <thead>
                  <tr style="background: #f8fafc;">
                    <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 6px 8px;">Receipt</th>
                    <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 6px 8px;">Qty Recv</th>
                    <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 6px 8px;">Recv Price</th>
                    <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 6px 8px;">PO Price</th>
                    <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 6px 8px;">Variance</th>
                    <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 6px 8px;">Status</th>
                    <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 6px 8px;">Approval</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pri in poItem.purchase_receipt_items" :key="pri.id"
                    :style="pri.status === 'not_received' ? 'opacity: 0.4;' : ''">
                    <td style="padding: 7px 8px; vertical-align: middle;">
                      <span style="font-weight: 600;">
                        {{ getReceiptInvoice(pri.receipt_id)?.invoice_number || '-' }}
                      </span>
                      <div style="font-size: 9px; color: #94a3b8;">
                        {{ formatDate(getReceiptInvoice(pri.receipt_id)?.created_at) }}
                      </div>
                    </td>
                    <td style="padding: 7px 8px; font-weight: 600; vertical-align: middle;">
                      {{ pri.quantity_received }}
                    </td>
                    <td style="padding: 7px 8px; vertical-align: middle;">
                      <span :style="pri.unit_price !== pri.po_price && pri.quantity_received > 0 ? 'color:#ef4444; font-weight:700;' : 'color:#1e293b;'">
                        {{ pri.quantity_received > 0 ? formatCurrency(pri.unit_price) : '-' }}
                      </span>
                    </td>
                    <td style="padding: 7px 8px; color: #64748b; vertical-align: middle;">
                      {{ formatCurrency(pri.po_price) }}
                    </td>
                    <td style="padding: 7px 8px; vertical-align: middle;">
                      <template v-if="pri.variance_pct !== null && pri.variance_pct !== 0">
                        <span style="font-weight: 700; font-size: 11px;"
                          :style="pri.variance_pct > 0 ? 'color:#ef4444;' : 'color:#16a34a;'">
                          {{ pri.variance_pct > 0 ? '+' : '' }}{{ pri.variance_pct }}%
                        </span>
                      </template>
                      <span v-else-if="pri.status === 'not_received'" style="color: #cbd5e1;">—</span>
                      <span v-else style="color: #16a34a; font-size: 11px;">0%</span>
                    </td>
                    <td style="padding: 7px 8px; vertical-align: middle;">
                      <span class="badge rounded-pill" style="font-size: 9.5px;"
                        :class="{
                          'bg-success-light text-success': pri.status === 'ok',
                          'bg-warning-light text-warning': pri.status === 'pending',
                          'bg-info-light text-info': pri.status === 'approved',
                          'bg-danger-light text-danger': pri.status === 'rejected',
                          'bg-light text-muted': pri.status === 'not_received'
                        }"
                        :style="{
                          'background': pri.status === 'ok' ? '#f0fdf4' : pri.status === 'pending' ? '#fffbeb' : pri.status === 'approved' ? '#eff6ff' : pri.status === 'rejected' ? '#fef2f2' : '#f8fafc',
                          'color': pri.status === 'ok' ? '#16a34a' : pri.status === 'pending' ? '#d97706' : pri.status === 'approved' ? '#2563eb' : pri.status === 'rejected' ? '#ef4444' : '#94a3b8',
                          'border': '1px solid',
                          'border-color': pri.status === 'ok' ? '#bbf7d0' : pri.status === 'pending' ? '#fde68a' : pri.status === 'approved' ? '#bfdbfe' : pri.status === 'rejected' ? '#fecaca' : '#e2e8f0'
                        }">
                        {{ pri.status === 'ok' ? '✓ OK' : pri.status === 'pending' ? '⏳ Pending' : pri.status === 'approved' ? '✓ Approved' : pri.status === 'rejected' ? '✗ Rejected' : '— Not Received' }}
                      </span>
                    </td>
                    <td style="padding: 7px 8px; vertical-align: middle;">
                      <template v-if="pri.stock_approval">
                        <div style="font-size: 10px;">
                          <span style="font-weight: 600;" :style="pri.stock_approval.status === 'approved' ? 'color:#16a34a;' : 'color:#ef4444;'">
                            {{ pri.stock_approval.status === 'approved' ? 'Approved' : 'Rejected' }}
                          </span>
                          <span style="color: #94a3b8;"> by {{ pri.stock_approval.actioned_by?.name }}</span>
                        </div>
                        <div v-if="pri.stock_approval.remark" style="font-size: 10px; color: #64748b; font-style: italic; margin-top: 1px;">
                          "{{ pri.stock_approval.remark }}"
                        </div>
                        <div style="font-size: 9px; color: #94a3b8; margin-top: 1px;">
                          {{ formatDate(pri.stock_approval.created_at) }}
                        </div>
                        <a v-if="pri.stock_approval.document_url" :href="pri.stock_approval.document_url" target="_blank"
                          style="font-size: 10px; color: #2563eb; text-decoration: none; display: inline-flex; align-items: center; gap: 2px; margin-top: 2px;">
                          <i class="ti ti-file-check" style="font-size: 11px;"></i> View Doc
                        </a>
                      </template>
                      <span v-else style="color: #cbd5e1; font-size: 10px;">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>

        <!-- Receipts / Invoices List -->
        <div style="padding: 0 22px 16px;">
          <h6 style="font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 10px; display: flex; align-items: center; gap: 6px;">
            <i class="ti ti-file-invoice" style="color: #3b82f6;"></i> Invoices / Receipts
          </h6>
          <div style="background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden;">
            <table class="table table-sm mb-0" style="font-size: 11.5px;">
              <thead>
                <tr style="background: #f8fafc;">
                  <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 8px 10px;">#</th>
                  <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 8px 10px;">Invoice No.</th>
                  <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 8px 10px;">Amount</th>
                  <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 8px 10px;">Items</th>
                  <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 8px 10px;">Date</th>
                  <th style="font-size: 10px; font-weight: 700; color: #fff; padding: 8px 10px;">Invoice</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(receipt, rIdx) in selectedPO.receipts" :key="receipt.id">
                  <td style="padding: 8px 10px;">{{ rIdx + 1 }}</td>
                  <td style="padding: 8px 10px; font-weight: 600;">{{ receipt.invoice_number }}</td>
                  <td style="padding: 8px 10px;">{{ formatCurrency(receipt.total_amount) }}</td>
                  <td style="padding: 8px 10px;">{{ receipt.total_items }}</td>
                  <td style="padding: 8px 10px; color: #64748b;">{{ formatDate(receipt.created_at) }}</td>
                  <td style="padding: 8px 10px;">
                    <a v-if="receipt.files && receipt.files.length" href="javascript:void(0);" @click="viewInvoiceImage(receipt)"
                      style="color: #2563eb; text-decoration: none; display: inline-flex; align-items: center; gap: 3px; font-size: 11px;">
                      <i class="ti ti-photo" style="font-size: 13px;"></i>
                      View ({{ receipt.files.length }})
                    </a>
                    <span v-else style="color: #cbd5e1;">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div><!-- /modal-body -->

      <!-- Footer -->
      <div class="modal-footer" style="border-top: 1px solid #e2e8f0; padding: 10px 22px;">
        <button class="btn btn-sm btn-light" data-bs-dismiss="modal">Close</button>
      </div>

    </div>
  </div>
</div>
<!-- /PO DETAIL MODAL -->
</template>
<style scoped>
.table-responsive {
  overflow-y: auto;
}
</style>
